import { ApolloClient } from '@apollo/client';
import { Dispatch } from 'redux';
import { Map } from 'immutable';

import CauseApi from 'entities/cause/api';
import Cause, { CauseTypes } from 'entities/cause/model';

import {
  CallActionPayload,
  MethodResponse,
  UIStateCallback,
  ObjectContextValue,
} from '../../types';
import { ContextAPI } from '../ImpactAiContextProvider';

interface TransformedSDG extends ObjectContextValue {
  id: string;
  name: string;
  description: string;
  number: number;
}

interface TransformedCategory extends ObjectContextValue {
  id: string;
  name: string;
  description: string;
}

interface CauseAction {
  type: string;
  causes?: Map<string, Cause>;
}

interface LoadCausesPayload {
  sdgsOutputPath?: string;
  categoriesOutputPath?: string;
  waitForCompletion?: boolean;
}

export const loadCauses = async (
  payload: CallActionPayload,
  client: ApolloClient<unknown>,
  contextApi: ContextAPI,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>,
  uiStateCallback?: UIStateCallback
): Promise<MethodResponse> => {
  const {
    sdgsOutputPath = 'sdgs',
    categoriesOutputPath = 'categories',
    waitForCompletion = true,
  } = payload as LoadCausesPayload;

  if (uiStateCallback) {
    uiStateCallback('processing');
  }

  // Create the actual loading function
  const performLoad = async (): Promise<void> => {
    const action: CauseAction = await CauseApi.fetchAll().toPromise();

    if (action && action.type) {
      dispatch(action);
    }

    const allCauses: Cause[] = action?.causes
      ? Object.values(action.causes.toJS() as { [key: string]: Cause })
      : [];

    // Filter SDGs and Categories
    const sdgs: TransformedSDG[] = allCauses
      .filter((cause: Cause) => cause?.type === CauseTypes.SDG)
      .sort((a: Cause, b: Cause) => (a.number || 0) - (b.number || 0))
      .map((cause: Cause) => ({
        id: cause.id,
        name: cause.name,
        description: cause.description,
        number: cause.number,
      }));

    const categories: TransformedCategory[] = allCauses
      .filter((cause: Cause) => cause?.type === CauseTypes.Category)
      .sort((a: Cause, b: Cause) => a.name.localeCompare(b.name))
      .map((cause: Cause) => ({
        id: cause.id,
        name: cause.name,
        description: cause.description,
      }));

    contextApi.setValue(sdgsOutputPath, sdgs);
    contextApi.setValue(categoriesOutputPath, categories);
  };

  if (!waitForCompletion) {
    performLoad()
      .then(() => {
        if (uiStateCallback) {
          uiStateCallback('complete');
        }
      })
      .catch((error) => {
        console.error('Background loadCauses failed:', error);
      });

    if (uiStateCallback) {
      uiStateCallback('complete');
    }

    return {};
  }

  try {
    await performLoad();

    if (uiStateCallback) {
      uiStateCallback('complete');
    }

    return {};
  } catch (error) {
    const handleRetry = async (): Promise<MethodResponse> => {
      try {
        return await loadCauses(payload, client, contextApi, dispatch, uiStateCallback);
      } catch (retryError) {
        if (uiStateCallback) {
          uiStateCallback('error', handleRetry);
        }
        return { error: true };
      }
    };

    if (uiStateCallback) {
      uiStateCallback('error', handleRetry);
    }
    return { error: true };
  }
};
